import React from "react"
import StatusPill from "../../../shared/components/StatusPill"
import { buttonStyle, primaryButtonStyle } from "./ClinicalHistoryStyles"

function ContactLine({ label, value }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        alignItems: "center",
        flexWrap: "wrap",
        fontSize: 13,
        color: "#475569",
        lineHeight: 1.4,
      }}
    >
      <span
        style={{
          fontSize: 11,
          fontWeight: 900,
          textTransform: "uppercase",
          letterSpacing: 0.4,
          color: "#64748b",
        }}
      >
        {label}
      </span>

      <span
        style={{
          color: "#334155",
          fontWeight: 600,
          wordBreak: "break-word",
        }}
      >
        {value}
      </span>
    </div>
  )
}

function ContextPill({ children, tone = "neutral" }) {
  return (
    <StatusPill tone={tone} style={{ whiteSpace: "nowrap" }}>
      {children}
    </StatusPill>
  )
}

function SummaryTile({ title, value, subtitle, tone = "neutral" }) {
  const visual =
    tone === "danger"
      ? { bg: "#fff7ed", border: "#fed7aa", value: "#c2410c" }
      : tone === "info"
        ? { bg: "#eff6ff", border: "#bfdbfe", value: "#1d4ed8" }
        : tone === "success"
          ? { bg: "#f0fdf4", border: "#bbf7d0", value: "#166534" }
          : { bg: "#f8fafc", border: "#e2e8f0", value: "#0f172a" }

  return (
    <div
      style={{
        borderRadius: 14,
        border: `1px solid ${visual.border}`,
        background: visual.bg,
        padding: 10,
        display: "grid",
        gap: 4,
        alignContent: "start",
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 800,
          color: "#64748b",
          textTransform: "uppercase",
          letterSpacing: 0.3,
        }}
      >
        {title}
      </div>

      <div
        style={{
          fontSize: 20,
          lineHeight: 1.05,
          fontWeight: 900,
          color: visual.value,
          wordBreak: "break-word",
        }}
      >
        {value}
      </div>

      <div
        style={{
          fontSize: 11.5,
          color: "#64748b",
          lineHeight: 1.35,
        }}
      >
        {subtitle}
      </div>
    </div>
  )
}

export default function ClinicalPatientHero({
  patientDisplayName,
  hub,
  age,
  form,
  isFormDirty,
  isReadOnly,
  readOnlyReason,
  saving,
  onBackToLauncher,
  onOpenPatientsProfile,
  onOpenBaseEditor,
  onOpenOdontogram,
}) {
  const initials =
    patientDisplayName
      ?.split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase() || "P"

  const baseHistoryExists = Boolean(hub?.summary?.baseHistoryExists)
  const latestBaseHistoryAt = hub?.summary?.latestBaseHistoryAt
    ? new Date(hub.summary.latestBaseHistoryAt).toLocaleDateString("es-MX")
    : "Sin registro"

  const heroStatus = !baseHistoryExists
    ? { tone: "warning", label: "Historia base pendiente" }
    : isFormDirty
      ? { tone: "warning", label: "Cambios pendientes" }
      : { tone: "info", label: "Continuidad activa" }

  const primaryAction =
    !baseHistoryExists || isFormDirty
      ? {
          label: "Abrir historia base",
          onClick: onOpenBaseEditor,
        }
      : {
          label: "Abrir odontograma",
          onClick: () => onOpenOdontogram(false),
        }

  const secondaryActions =
    primaryAction.label === "Abrir historia base"
      ? [
          { label: "Cambiar paciente", onClick: onBackToLauncher },
          { label: "Ver ficha", onClick: onOpenPatientsProfile },
          { label: "Abrir odontograma", onClick: () => onOpenOdontogram(false) },
        ]
      : [
          { label: "Cambiar paciente", onClick: onBackToLauncher },
          { label: "Ver ficha", onClick: onOpenPatientsProfile },
          { label: "Abrir historia base", onClick: onOpenBaseEditor },
        ]

  return (
    <div
      style={{
        minWidth: 0,
        border: "1px solid #e2e8f0",
        borderRadius: 22,
        background: "#ffffff",
        boxShadow: "0 10px 24px rgba(15,23,42,0.05)",
        padding: 14,
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) minmax(280px, 360px)",
          gap: 12,
          alignItems: "start",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "52px minmax(0,1fr)",
            gap: 12,
            alignItems: "start",
            minWidth: 0,
          }}
        >
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 15,
              background: "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)",
              color: "#ffffff",
              display: "grid",
              placeItems: "center",
              fontWeight: 900,
              fontSize: 18,
              boxShadow: "0 8px 18px rgba(37,99,235,0.16)",
              flexShrink: 0,
            }}
          >
            {initials}
          </div>

          <div style={{ display: "grid", gap: 9, minWidth: 0 }}>
            <div style={{ display: "grid", gap: 6 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 900,
                  textTransform: "uppercase",
                  letterSpacing: 0.6,
                  color: "#64748b",
                }}
              >
                Resumen clínico
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <div
                  style={{
                    fontSize: 24,
                    fontWeight: 900,
                    color: "#0f172a",
                    lineHeight: 1.02,
                    letterSpacing: "-0.03em",
                    wordBreak: "break-word",
                  }}
                >
                  {patientDisplayName}
                </div>

                <StatusPill tone={heroStatus.tone}>{heroStatus.label}</StatusPill>
              </div>

              <div
                style={{
                  fontSize: 12.5,
                  color: "#64748b",
                  lineHeight: 1.45,
                  maxWidth: 720,
                }}
              >
                Decide la siguiente acción clínica sin romper continuidad ni mezclar
                navegación, captura y seguimiento.
              </div>
            </div>

            <ContactLine
              label="Teléfono"
              value={form?.phone || "Sin teléfono registrado"}
            />

            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <ContextPill tone="neutral">Edad: {age || "—"}</ContextPill>

              <ContextPill tone={baseHistoryExists ? "success" : "warning"}>
                Historia base: {baseHistoryExists ? "Sí" : "No"}
              </ContextPill>

              {hub?.summary?.latestBaseHistoryAt ? (
                <ContextPill tone="info">
                  Última base: {latestBaseHistoryAt}
                </ContextPill>
              ) : null}

              {isReadOnly ? (
                <ContextPill tone="danger">
                  {readOnlyReason || "Solo lectura"}
                </ContextPill>
              ) : null}
            </div>

            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              {secondaryActions.map((action) => (
                <button
                  key={action.label}
                  onClick={action.onClick}
                  style={buttonStyle}
                >
                  {action.label}
                </button>
              ))}

              <button
                onClick={primaryAction.onClick}
                disabled={saving}
                style={{
                  ...primaryButtonStyle,
                  cursor: saving ? "not-allowed" : "pointer",
                  opacity: saving ? 0.7 : 1,
                }}
              >
                {primaryAction.label}
              </button>
            </div>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gap: 10,
            alignContent: "start",
            minWidth: 0,
          }}
        >
          <SummaryTile
            title="Seguimiento"
            value={latestBaseHistoryAt}
            subtitle={
              hub?.summary?.latestBaseHistoryAt
                ? "Última actualización de historia base"
                : "Sin actualización clínica general"
            }
            tone={hub?.summary?.latestBaseHistoryAt ? "info" : "neutral"}
          />

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0,1fr))",
              gap: 10,
            }}
          >
            <SummaryTile
              title="Citas"
              value={String(hub?.summary?.appointmentsCount || 0)}
              subtitle="Actividad clínica registrada"
              tone={hub?.summary?.appointmentsCount > 0 ? "info" : "neutral"}
            />

            <SummaryTile
              title="Planes"
              value={String(hub?.summary?.treatmentPlansCount || 0)}
              subtitle={
                hub?.summary?.treatmentPlansCount > 0
                  ? "Tratamientos vinculados"
                  : "Sin tratamientos vinculados"
              }
              tone={hub?.summary?.treatmentPlansCount > 0 ? "info" : "neutral"}
            />

            <SummaryTile
              title="Formularios"
              value={String(hub?.summary?.specialtyFormsCount || 0)}
              subtitle={
                hub?.summary?.specialtyFormsCount > 0
                  ? "Registros especializados"
                  : "Sin formularios especializados"
              }
              tone={hub?.summary?.specialtyFormsCount > 0 ? "success" : "neutral"}
            />

            <SummaryTile
              title="Estado base"
              value={baseHistoryExists ? "Completa" : "Pendiente"}
              subtitle={
                isFormDirty
                  ? "Hay cambios todavía sin consolidar"
                  : "Estado actual del expediente base"
              }
              tone={!baseHistoryExists || isFormDirty ? "danger" : "success"}
            />
          </div>
        </div>
      </div>
    </div>
  )
}